'use client';

import { useMemo } from 'react';
import { useUser } from '@/lib/context/user-context';
import { useUserSettings } from '@/contexts/user-settings-context';
import { toast } from 'sonner';

export const useFavorites = () => {
  const { user } = useUser();
  const { userSettings, setUserSettings } = useUserSettings();

  const toggleFavorite = async (episodeId: string) => {
    console.log('🌟 toggleFavorite called with episodeId:', episodeId);
    console.log('🌟 User ID:', user?.id);
    console.log('🌟 Current favorites:', userSettings.favorites);
    
    if (!user?.id) {
      console.warn('🌟 No user logged in');
      toast.error('Please login to add favorites');
      return;
    }

    if (!episodeId) {
      console.warn('🌟 No episode ID provided');
      toast.error('Invalid episode ID');
      return;
    }

    try {
      const currentFavorites = userSettings.favorites || [];
      const isFavorited = currentFavorites.includes(episodeId);
      
      console.log('🌟 Is currently favorited:', isFavorited);
      console.log('🌟 Current favorites list:', currentFavorites);
      
      const newFavorites = isFavorited 
        ? currentFavorites.filter(id => id !== episodeId)
        : [...currentFavorites, episodeId];

      // 更新本地状态（乐观更新）
      setUserSettings(prev => ({ ...prev, favorites: newFavorites }));

      // 调用 API 更新后端
      const response = await fetch('/api/user-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          settingKey: 'favorites',
          value: newFavorites
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update favorites');
      }

      // 显示成功消息
      toast.success(
        isFavorited ? 'Removed from favorites' : 'Added to favorites'
      );

    } catch (error) {
      console.error('Error toggling favorite:', error);
      
      // 回滚本地状态
      const currentFavorites = userSettings.favorites || [];
      setUserSettings(prev => ({ ...prev, favorites: currentFavorites }));
      
      toast.error('Failed to update favorites. Please try again.');
    }
  };

  const addToFavorites = async (episodeId: string) => {
    if (!userSettings.favorites?.includes(episodeId)) {
      await toggleFavorite(episodeId);
    }
  };

  const removeFromFavorites = async (episodeId: string) => {
    if (userSettings.favorites?.includes(episodeId)) {
      await toggleFavorite(episodeId);
    }
  };

  const isFavorited = (episodeId: string): boolean => {
    if (!episodeId) return false;
    return userSettings.favorites?.includes(episodeId) || false;
  };

  const favorites = useMemo(() => userSettings.favorites ?? [], [userSettings.favorites]);

  return {
    favorites,
    toggleFavorite,
    addToFavorites,
    removeFromFavorites,
    isFavorited,
    favoritesCount: favorites.length
  };
};
